import { useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import { CalendarPlus, Check, CheckCircle2, Copy, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AvailabilitySlot, PublicService, PublicShop } from '@agenda-facil/contracts';
import { Button } from '@agenda-facil/ui';

export interface AppointmentResult {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  confirmationCode: string;
}

interface TicketConfirmationProps {
  shop: PublicShop;
  service: PublicService;
  slot: AvailabilitySlot;
  appointment: AppointmentResult;
}

function calendarTimestamp(value: string): string {
  return new Date(value)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function escapeCalendarText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/([,;])/g, '\\$1')
    .replace(/\n/g, '\\n');
}

export function buildCalendarLinks({ shop, service, slot, appointment }: TicketConfirmationProps) {
  const title = `${service.name} em ${shop.name}`;
  const details = `Profissional: ${slot.barberName}\nCódigo: ${appointment.confirmationCode}`;
  const startsAt = calendarTimestamp(appointment.startsAt);
  const endsAt = calendarTimestamp(appointment.endsAt);
  const google = new URL('https://calendar.google.com/calendar/render');
  google.searchParams.set('action', 'TEMPLATE');
  google.searchParams.set('text', title);
  google.searchParams.set('dates', `${startsAt}/${endsAt}`);
  google.searchParams.set('details', details);
  if (shop.address) google.searchParams.set('location', shop.address);

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Agenda Facil//Agendamento//PT-BR',
    'BEGIN:VEVENT',
    `UID:${appointment.id}@agenda.painelmv.com.br`,
    `DTSTAMP:${calendarTimestamp(new Date().toISOString())}`,
    `DTSTART:${startsAt}`,
    `DTEND:${endsAt}`,
    `SUMMARY:${escapeCalendarText(title)}`,
    `DESCRIPTION:${escapeCalendarText(details)}`,
    `LOCATION:${escapeCalendarText(shop.address)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return {
    google: google.toString(),
    ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`,
  };
}

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function TicketConfirmation({ shop, service, slot, appointment }: TicketConfirmationProps) {
  const [copied, setCopied] = useState(false);
  const formattedDate = formatInTimeZone(
    appointment.startsAt,
    shop.timezone,
    "EEEE, dd 'de' MMMM 'de' yyyy",
    { locale: ptBR },
  );
  const formattedTime = formatInTimeZone(appointment.startsAt, shop.timezone, 'HH:mm');
  const calendars = buildCalendarLinks({ shop, service, slot, appointment });
  const isConfirmed = appointment.status === 'CONFIRMED';

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(appointment.confirmationCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const whatsappMessage = encodeURIComponent(
    `Agendamento em ${shop.name}\n\n` +
      `Serviço: ${service.name}\n` +
      `Profissional: ${slot.barberName}\n` +
      `Data: ${formattedDate}\n` +
      `Horário: ${formattedTime}\n` +
      `Código: ${appointment.confirmationCode}`,
  );

  return (
    <main className="motion-confirmation-in grid min-h-screen place-items-center bg-[var(--background-deep)] px-4 py-10 sm:py-14">
      <div className="w-full max-w-md">
        <div className="ticket-container border border-[var(--border-strong)]">
          <div className="barber-stripe h-3" aria-hidden="true" />

          <header className="border-b border-[var(--border)] bg-[var(--surface-raised)] p-6 text-center">
            <span className="motion-confirmation-mark mx-auto mb-3 grid size-14 place-items-center rounded-full bg-[var(--success-soft)] text-[var(--success)] shadow-[var(--shadow-sm)]">
              <CheckCircle2 size={30} aria-hidden="true" />
            </span>
            <span className="eyebrow">
              {isConfirmed ? 'Agendamento confirmado' : 'Solicitação recebida'}
            </span>
            <h1 className="display mt-2 text-2xl font-black text-[var(--foreground)]">
              {shop.name}
            </h1>
            <p className="mt-1 text-xs leading-5 text-[var(--muted-foreground)]">
              {isConfirmed
                ? 'Guarde este comprovante para consultar os detalhes.'
                : 'A barbearia ainda confirmará este horário.'}
            </p>
          </header>

          <div
            className="ticket-cutout-notch flex items-center justify-center bg-[var(--surface)] py-2"
            aria-hidden="true"
          >
            <div className="w-[85%] border-t-2 border-dashed border-[var(--border-strong)]" />
          </div>

          <div className="space-y-4 p-5 text-xs sm:p-6">
            <div className="rounded-2xl border border-[var(--border)] bg-[var(--input)] p-4 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
                Código de confirmação
              </p>
              <div className="mt-2 flex items-center justify-center gap-2">
                <span className="font-mono text-2xl font-black tracking-[0.16em] text-[var(--brand)]">
                  {appointment.confirmationCode}
                </span>
                <button
                  type="button"
                  onClick={() => void copyCode()}
                  className="grid size-11 place-items-center rounded-xl bg-[var(--surface-raised)] text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                  aria-label={copied ? 'Código copiado' : 'Copiar código'}
                >
                  {copied ? (
                    <Check size={17} className="text-[var(--success)]" />
                  ) : (
                    <Copy size={17} />
                  )}
                </button>
              </div>
            </div>

            <dl className="grid gap-3 border-y border-dashed border-[var(--border-strong)] py-4">
              <TicketRow label="Serviço" value={service.name} />
              <TicketRow label="Profissional" value={slot.barberName} />
              <TicketRow label="Data" value={formattedDate} />
              <TicketRow label="Horário" value={formattedTime} />
              <TicketRow label="Duração" value={`${service.durationMinutes} min`} />
              <TicketRow label="Valor" value={currency.format(service.priceCents / 100)} />
              <TicketRow
                label="Status"
                value={isConfirmed ? 'Confirmado' : 'Aguardando confirmação'}
              />
            </dl>

            <div>
              <p className="mb-2 text-[10px] font-black uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
                Adicionar ao calendário
              </p>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={calendars.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-center text-xs font-extrabold text-[var(--foreground)] transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                >
                  <CalendarPlus size={15} aria-hidden="true" /> Google
                </a>
                <a
                  href={calendars.ics}
                  download={`agendamento-${appointment.confirmationCode}.ics`}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-center text-xs font-extrabold text-[var(--foreground)] transition-colors hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                >
                  <CalendarPlus size={15} aria-hidden="true" /> Apple
                </a>
              </div>
            </div>

            <div className="grid gap-2 pt-1">
              <Button asChild>
                <a
                  href={`https://wa.me/?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Share2 size={16} aria-hidden="true" /> Compartilhar no WhatsApp
                </a>
              </Button>
              <Button variant="secondary" asChild>
                <Link to={`/barbearia/${shop.slug}`}>Voltar para a barbearia</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function TicketRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-3">
      <dt className="font-bold text-[var(--muted-foreground)]">{label}</dt>
      <dd className="text-right font-extrabold capitalize text-[var(--foreground)]">{value}</dd>
    </div>
  );
}
